import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppScreen } from 'components/ds';
import { ScheduleMainContent } from 'components/schedule/ScheduleMainContent';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { MeetingRepository } from 'repository/meetingRepository';
import styles from './ScheduleMain.module.css';

export const ScheduleMain = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { t } = useTranslation();
    const [membersData, setMembersData] = useState<any>(null);
    const [statisticsData, setStatisticsData] = useState<any>(null);
    const [aiRecommendData, setAiRecommendData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // 컴포넌트 마운트 시 스크롤을 최상단으로 이동
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 컴포넌트 마운트 시 시간대별 통계 및 멤버 정보 조회
    useEffect(() => {
        const fetchData = async () => {
            if (meetingId) {
                try {
                    setLoading(true);

                    // 멤버 정보 조회
                    const membersResponse = await MeetingRepository.onGetMeetingMembers(meetingId);
                    console.log('멤버 정보:', membersResponse.data);
                    setMembersData(membersResponse.data?.data);

                    // 시간대별 통계 조회
                    const statisticsResponse = await ScheduleRepository.onGetTimeSlotStatistics(meetingId);
                    console.log('시간대별 통계:', statisticsResponse.data);
                    setStatisticsData(statisticsResponse.data?.data);

                    // AI 추천 일정 조회
                    try {
                        const aiResponse = await ScheduleRepository.onGetAiRecommendSchedule(meetingId);
                        console.log('AI 추천 일정 전체 응답:', aiResponse);
                        console.log('AI 추천 일정 data:', aiResponse.data);
                        console.log('AI 추천 일정 data.data:', aiResponse.data?.data);
                        setAiRecommendData(aiResponse.data?.data);
                    } catch (aiError) {
                        console.log('AI 추천 일정 조회 실패 (선택사항):', aiError);
                    }
                } catch (error) {
                    console.error('데이터 조회 실패:', error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [meetingId]);

    // API 데이터를 participants 형식으로 변환
    // statisticsData.timeSlots를 참여자별로 그룹화
    const participants = useMemo(() => {
        console.log('membersData:', membersData);
        console.log('statisticsData:', statisticsData);

        // membersData는 이미 배열 형태로 들어옴
        const result = membersData?.map((member: any) => {
            // 해당 참여자가 제안한 일정 목록 찾기
            const userSchedules: any[] = [];

            if (statisticsData?.timeSlots) {
                statisticsData.timeSlots.forEach((slot: any) => {
                    // proposers 배열에서 해당 사용자가 있는지 확인
                    const isProposer = slot.proposers?.some((proposer: any) =>
                        proposer.userNo === member.userNo
                    );

                    if (isProposer) {
                        userSchedules.push({
                            id: `${slot.date}-${slot.startTime}`,
                            date: slot.date, // YYYY-MM-DD
                            startTime: slot.startTime.substring(0, 5), // HH:mm:ss -> HH:mm
                            endTime: slot.endTime.substring(0, 5) // HH:mm:ss -> HH:mm
                        });
                    }
                });
            }

            return {
                id: member.userNo,
                name: (() => { const n = member.nickname || member.nickName || ''; if (!n) return t('scheduleMain.userFallback'); try { return decodeURIComponent(n); } catch { return n; } })(),
                avatar: member.profileUrl || '👤',
                schedules: userSchedules
            };
        }) || [];

        console.log('participants:', result);
        return result;
    }, [membersData, statisticsData]);

    const handleAddSchedule = () => {
        navigate(`/schedule/input/${meetingId}`);
    };

    const handleVote = () => {
        navigate(`/schedule/vote/${meetingId}`);
    };

    /* 장소 후보 투표 — 시간과 따로 정합니다. 방장이 후보를 올리고 참여자가 투표합니다. */
    const handlePlaceVote = () => {
        navigate(`/schedule/place-vote/${meetingId}`);
    };

    if (loading) {
        return (
            <AppScreen title={t('scheduleMain.title')} bar="sub">
                <p className={styles.state}>{t('scheduleMain.loading')}</p>
            </AppScreen>
        );
    }

    return (
        <AppScreen title={t('scheduleMain.title')} bar="sub">
            <ScheduleMainContent
                participants={participants}
                timeSlots={statisticsData?.timeSlots || []}
                aiRecommendations={aiRecommendData?.recommendations || []}
                onAddSchedule={handleAddSchedule}
                onVote={handleVote}
            />
            {/* 장소는 시간과 따로 정합니다 — 여기서 후보와 표를 봅니다 */}
            <div className={styles.placeVoteRow}>
                <button type="button" className={styles.placeVoteBtn} onClick={handlePlaceVote}>
                    {t('scheduleMain.goPlaceVote')}
                </button>
            </div>
        </AppScreen>
    );
};
